import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { MailAddress, MailboxAttachment, MailboxFolder, MailboxMessage, folderDisplayName } from '../_types/mailbox';

/// Cabecera, acciones y adjuntos de un mensaje. El cuerpo lo pinta `app-message-body`, aislado en un iframe.
@Component({
    selector: 'app-message-view',
    templateUrl: './message-view.component.html',
    styleUrls: ['./message-view.component.css'],
    standalone: false
})
export class MessageViewComponent {
  @Input() message: MailboxMessage | null = null;
  @Input() loading: boolean = false;
  @Input() bodyDocument: string | null = null;
  @Input() bodyUrl: string | null = null;
  @Input() allowRemoteImages: boolean = false;
  @Input() allowManage: boolean = false;
  @Input() allowSend: boolean = false;
  @Input() sourceUrl: string | null = null;

  /// Carpetas a las que se puede mover, ya aplanadas para el menú.
  @Input() set moveTargets(folders: MailboxFolder[]) {
    this.moveMenu = this.buildMoveMenu(folders);
  }

  @Output() allowRemoteImagesChange = new EventEmitter<boolean>();
  @Output() toggleSeen = new EventEmitter<void>();
  @Output() toggleFlagged = new EventEmitter<void>();
  @Output() deleteRequested = new EventEmitter<void>();
  @Output() moveRequested = new EventEmitter<string>();
  @Output() attachmentRequested = new EventEmitter<MailboxAttachment>();

  /// Responder, responder a todos o reenviar. El borrador lo prepara el servidor.
  @Output() composeRequested = new EventEmitter<'reply' | 'replyAll' | 'forward'>();

  public moveMenu: MenuItem[] = [];

  addressList(addresses: MailAddress[] | null | undefined): string {
    if (!addresses || addresses.length === 0) return '';
    return addresses.map(address => (address.name?.trim() ? `${address.name} <${address.address}>` : address.address)).join(', ');
  }

  senderLabel(): string {
    if (!this.message?.from) return '(sin remitente)';
    return this.message.from.name?.trim() || this.message.from.address;
  }

  /// Tamaño legible del adjunto.
  sizeLabel(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  iconFor(contentType: string): string {
    if (contentType?.startsWith('image/')) return 'pi pi-image';
    if (contentType === 'application/pdf') return 'pi pi-file-pdf';
    if (contentType?.includes('spreadsheet') || contentType?.includes('excel')) return 'pi pi-file-excel';
    if (contentType?.includes('word')) return 'pi pi-file-word';
    if (contentType === 'message/rfc822') return 'pi pi-envelope';
    return 'pi pi-file';
  }

  /// Aplana el árbol de carpetas para el menú «Mover a», con sangría según la profundidad.
  private buildMoveMenu(folders: MailboxFolder[]): MenuItem[] {
    const items: MenuItem[] = [];

    const walk = (list: MailboxFolder[], depth: number) => {
      for (const folder of list ?? []) {
        if (folder.selectable) {
          items.push({
            //el nombre traducido, igual que en el árbol: ver «Entrada» allí e «INBOX» aquí desconcertaría.
            //La sangría son espacios de no separación porque los normales se colapsan al pintar el menú.
            label: `${'\u00a0\u00a0'.repeat(depth)}${folderDisplayName(folder.name, folder.specialUse)}`,
            icon: depth === 0 ? 'pi pi-folder' : undefined,
            command: () => this.moveRequested.emit(folder.path)
          });
        }
        walk(folder.children ?? [], depth + 1);
      }
    };
    walk(folders ?? [], 0);

    return items;
  }
}
